/**
 * Java's text rules, for ports of desktop code whose results must match it exactly (the
 * dice engine, the random-table codec): what counts as whitespace, digits and letters,
 * `strip`, `lines`, `isBlank`, `Double.parseDouble`, `Math.round` and `Math.rint`. In
 * Java's regular expressions `\s` is ASCII only, so the ports use `JS_SPACE` instead of
 * JavaScript's `\s`, which also matches no-break and other Unicode spaces.
 */

/** Java's regex `\s`: space, tab, line feed, vertical tab, form feed, carriage return. */
export const JS_SPACE = '[ \\t\\n\\x0B\\f\\r]';
/** Java's regex `\S`. */
export const JS_NOT_SPACE = '[^ \\t\\n\\x0B\\f\\r]';

/** `Character.isWhitespace`. */
export function isJavaWhitespace(c: string): boolean {
  if (c === '\t' || c === '\n' || c === '\u000b' || c === '\f' || c === '\r' || (c >= '\u001c' && c <= '\u001f')) return true;
  return c.length === 1 && /[\p{Zs}\p{Zl}\p{Zp}]/u.test(c) && c !== ' ' && c !== ' ' && c !== ' ';
}

/** `Character.isDigit`, for any script's decimal digits. */
export const isJavaDigit = (c: string) => c.length === 1 && /\p{Nd}/u.test(c);
export const isAsciiDigit = (c: string) => c.length === 1 && c >= '0' && c <= '9';
/** `Character.isLetter`. */
export const isJavaLetter = (c: string) => c.length === 1 && /\p{L}/u.test(c);
/** `Character.isLetterOrDigit`. */
export const isJavaLetterOrDigit = (c: string) => isJavaLetter(c) || isJavaDigit(c);

/** `String.strip`. */
export function javaStrip(text: string): string {
  let start = 0;
  let end = text.length;
  while (start < end && isJavaWhitespace(text[start]!)) start++;
  while (end > start && isJavaWhitespace(text[end - 1]!)) end--;
  return text.slice(start, end);
}

/** `String.isBlank`. */
export const javaIsBlank = (text: string) => javaStrip(text) === '';

/** `String.lines`: split at \n, \r or \r\n, with no empty last line for a trailing break. */
export function javaLines(text: string): string[] {
  if (text === '') return [];
  const lines = text.split(/\r\n|\r|\n/);
  if (lines.at(-1) === '') lines.pop();
  return lines;
}

/** Java's regex `\R`: any line break. */
export const JAVA_LINE_BREAK = '(?:\\r\\n|[\\n\\x0B\\f\\r\\u0085\\u2028\\u2029])';

/**
 * `Double.parseDouble` for decimal numbers: Java trims characters up to space, allows a
 * trailing `d` or `f`, `NaN` and `Infinity`; null where Java throws. (Java's hexadecimal
 * floating-point notation is not read.)
 */
export function javaParseDouble(text: string): number | null {
  const trimmed = text.replace(/^[\u0000- ]+|[\u0000- ]+$/g, '');
  const match = /^([+-]?)(NaN|Infinity|(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)[fFdD]?$/.exec(trimmed);
  if (!match) return null;
  if (match[2] === 'NaN') return /[fFdD]$/.test(trimmed) ? null : NaN;
  if (match[2] === 'Infinity') return /[fFdD]$/.test(trimmed) ? null : match[1] === '-' ? -Infinity : Infinity;
  const value = Number(match[2]);
  return match[1] === '-' ? -value : value;
}

const LONG_MIN = -(2 ** 63);
const LONG_MAX = 2 ** 63;

/** `Math.round(double)`: floor(x + 0.5), NaN as 0, clamped to a long. */
export function javaRound(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value <= LONG_MIN) return LONG_MIN;
  if (value >= LONG_MAX) return LONG_MAX;
  return Math.floor(value + 0.5);
}

/** `Math.rint`: to the nearest whole number, halves to the even one. */
export function javaRint(value: number): number {
  if (!Number.isFinite(value)) return value;
  const floor = Math.floor(value);
  const diff = value - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

/** `(int)` of a long: its low 32 bits, as Java casts. */
export const javaToInt = (value: number) => Number(BigInt.asIntN(32, BigInt(Math.trunc(value))));

/** `Double.toString`: `5.0`, `0.25`, `1.0E10`, `1.0E-5`. */
export function javaDoubleToString(value: number): string {
  if (Number.isNaN(value)) return 'NaN';
  if (value === Infinity) return 'Infinity';
  if (value === -Infinity) return '-Infinity';
  if (value === 0) return Object.is(value, -0) ? '-0.0' : '0.0';
  const abs = Math.abs(value);
  if (abs >= 1e-3 && abs < 1e7) {
    const text = String(value);
    return text.includes('.') ? text : `${text}.0`;
  }
  const [mantissa, exponent] = value.toExponential().split('e') as [string, string];
  return `${mantissa.includes('.') ? mantissa : `${mantissa}.0`}E${Number(exponent)}`;
}

/** A decimal digit's value, as `Character.digit(c, 10)`: Unicode digits come in runs of ten from zero. */
function digitValue(c: string): number {
  const code = c.codePointAt(0)!;
  let start = code;
  while (start > 0 && /\p{Nd}/u.test(String.fromCodePoint(start - 1))) start--;
  return (code - start) % 10;
}

function parseJavaInteger(text: string, bits: bigint): bigint | null {
  const match = /^([+-]?)(\p{Nd}+)$/u.exec(text);
  if (!match) return null;
  let value = 0n;
  for (const c of match[2]!) value = value * 10n + BigInt(digitValue(c));
  if (match[1] === '-') value = -value;
  const limit = 2n ** (bits - 1n);
  return value < -limit || value >= limit ? null : value;
}

/** `Long.parseLong`: an optional sign and decimal digits (any script's) in a long's range, or null where Java throws. */
export const javaParseLong = (text: string): bigint | null => parseJavaInteger(text, 64n);

/** `Integer.parseInt`, as {@link javaParseLong} for an int. */
export function javaParseInt(text: string): number | null {
  const value = parseJavaInteger(text, 32n);
  return value === null ? null : Number(value);
}

/** One UTF-16 unit cased as `Character.toUpperCase(char)` would, never growing ("ß" stays "ß"). */
const upperUnit = (c: string) => {
  const upper = c.toUpperCase();
  return upper.length === 1 ? upper : c;
};
const lowerUnit = (c: string) => {
  const lower = c.toLowerCase();
  return lower.length === 1 ? lower : c;
};

/** `String.equalsIgnoreCase`: the same length, unit by unit equal in upper or lower case. */
export function javaEqualsIgnoreCase(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (x === y) continue;
    const ux = upperUnit(x);
    const uy = upperUnit(y);
    if (ux !== uy && lowerUnit(ux) !== lowerUnit(uy)) return false;
  }
  return true;
}

/** A number written out in full, without an exponent, from its shortest decimal form. */
function plainDecimal(abs: number): string {
  const text = String(abs);
  const m = /^(\d+)(?:\.(\d+))?e([+-]\d+)$/.exec(text);
  if (!m) return text;
  const digits = m[1]! + (m[2] ?? '');
  const point = m[1]!.length + Number(m[3]);
  if (point <= 0) return `0.${'0'.repeat(-point)}${digits}`;
  if (point >= digits.length) return digits + '0'.repeat(point - digits.length);
  return `${digits.slice(0, point)}.${digits.slice(point)}`;
}

/**
 * `String.format("%.Nf")` (and `"%,.Nf"` with `grouping`): Java rounds the shortest decimal
 * form of the number half up, so 1.005 is "1.01" to two places, where `toFixed` gives "1.00".
 */
export function javaFixed(value: number, digits: number, grouping = false): string {
  if (Number.isNaN(value)) return 'NaN';
  if (!Number.isFinite(value)) return value > 0 ? 'Infinity' : '-Infinity';
  const [whole, fraction = ''] = plainDecimal(Math.abs(value)).split('.');
  let kept = BigInt(whole! + fraction.padEnd(digits, '0').slice(0, digits));
  if ((fraction[digits] ?? '0') >= '5') kept += 1n;
  const text = kept.toString().padStart(digits + 1, '0');
  let intPart = text.slice(0, text.length - digits);
  if (grouping) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const sign = value < 0 || Object.is(value, -0) ? '-' : '';
  return `${sign}${intPart}${digits > 0 ? `.${text.slice(text.length - digits)}` : ''}`;
}
